import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import { Search, Database, Gamepad2, ChevronLeft, ChevronRight, Clock, Filter, Loader2, X, BarChart3, Globe, Trophy, MessageSquarePlus, Calendar, Layers } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import PlatformBadge, { SystemBadge, XBOX_LOGO_GREEN } from "@/components/PlatformBadge";
import SlopBadge from "@/components/SlopBadge";
import UnplayableBadge from "@/components/UnplayableBadge";
import { AdBanner } from "@/components/AdSense";
import GameRequestDialog from "@/components/GameRequestDialog";

const API = `${getApiUrl()}/api`;

// Collapsed version-group tiles link to the Version Hub; everything else links
// straight to the game info page.
const browseTarget = (game) =>
  (game.versionCount > 1 && game.versionGroupId)
    ? `/browse/version/${game.versionGroupId}`
    : `/browse/${game.id}`;

// Relationship role tag styles for Browse tiles.
const RELATION_TAG_STYLES = {
  Collection: "bg-indigo-500/20 text-indigo-300 border-indigo-500/30",
  Remake: "bg-orange-500/20 text-orange-300 border-orange-500/30",
  Remaster: "bg-teal-500/20 text-teal-300 border-teal-500/30",
  Alt: "bg-slate-500/25 text-slate-300 border-slate-500/40",
};

// Flip tile — cycles through assigned games with a 3D flip animation
function FlipTile({ games, interval = 4000, delay = 0 }) {
  const [index, setIndex] = useState(0);
  const [flipping, setFlipping] = useState(false);
  const timeoutRef = useRef(null);
  const timeoutRef2 = useRef(null);
  const hoveredRef = useRef(false);

  useEffect(() => {
    if (games.length <= 1) return;
    const tick = () => {
      if (hoveredRef.current) return; // skip flip while hovered
      setFlipping(true);
      // Wait for flip-out (700ms transition), pause briefly, then swap and flip back in
      timeoutRef.current = setTimeout(() => {
        setIndex(prev => (prev + 1) % games.length);
        timeoutRef2.current = setTimeout(() => {
          setFlipping(false);
        }, 150);
      }, 700);
    };
    const id = setInterval(tick, interval);
    const startId = setTimeout(tick, delay);
    return () => { clearInterval(id); clearTimeout(startId); clearTimeout(timeoutRef.current); clearTimeout(timeoutRef2.current); };
  }, [games, interval, delay]);

  const game = games[index];
  if (!game) return null;

  return (
    <Link
      to={browseTarget(game)}
      className="group block"
      title={game.name}
      onMouseEnter={() => { hoveredRef.current = true; }}
      onMouseLeave={() => { hoveredRef.current = false; }}
    >
      <div
        className="aspect-[4/5] relative overflow-hidden rounded-md bg-[#121212] border border-white/5 hover:border-purple-500/50 transition-colors"
        style={{ perspective: "600px" }}
      >
        <div
          className="w-full h-full transition-transform duration-700 ease-in-out"
          style={{
            transformStyle: "preserve-3d",
            transform: flipping ? "rotateY(90deg)" : "rotateY(0deg)",
          }}
        >
          {game.boxArtUrl ? (
            <img
              src={game.boxArtUrl}
              alt={game.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Gamepad2 className="w-5 h-5 text-white/20" />
            </div>
          )}
        </div>
      </div>
      <p className="text-[10px] text-white/40 mt-1 truncate text-center group-hover:text-white/70 transition-colors">{game.name}</p>
      {(game.isAchievementSlop || game.isUnplayable) && (
        <div className="flex items-center justify-center gap-1 mt-0.5">
          {game.isAchievementSlop && <SlopBadge compact />}
          {game.isUnplayable && <UnplayableBadge compact />}
        </div>
      )}
      {(game.platforms?.length > 0 || game.systems?.length > 0) && (
        <div className="flex items-center justify-center gap-1 mt-0.5">
          {game.platforms?.[0] && <PlatformBadge platform={game.platforms[0]} size="xs" showLabel={false} />}
          {game.systems?.[0] && <span className="text-[9px] text-white/30 truncate">{game.primarySystem || game.systems[0]}</span>}
        </div>
      )}
    </Link>
  );
}

// Region labels
const REGION_LABELS = {
  "NA": "North America",
  "EU": "Europe",
  "JP": "Japan",
  "AS": "Asia",
  "WW": "Worldwide",
  "AU": "Australia",
  "KR": "Korea",
};

// Debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function Browse() {
  const [stats, setStats] = useState(null);
  const [regionNames, setRegionNames] = useState({});
  const [recentGames, setRecentGames] = useState([]);
  const [featuredGames, setFeaturedGames] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingGames, setLoadingGames] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const [selectedSystem, setSelectedSystem] = useState("all");
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [selectedMediaType, setSelectedMediaType] = useState("all");
  const [selectedOwnership, setSelectedOwnership] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [gameRequestOpen, setGameRequestOpen] = useState(false);
  const [unreadIds, setUnreadIds] = useState([]);
  const [showNewOnly, setShowNewOnly] = useState(false);
  const [newGames, setNewGames] = useState([]);
  const [loadingNewGames, setLoadingNewGames] = useState(false);
  const unreadSet = new Set(unreadIds);
  const [pageSize, setPageSize] = useState(() => {
    // Try to get from localStorage, default to 50
    const saved = localStorage.getItem("browsePageSize");
    return saved ? parseInt(saved, 10) : 50;
  });
  
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Admin-uploaded platform logos (keyed by normalized id/name), shared app-wide.
  const [platformIcons, setPlatformIcons] = useState({});
  const normKey = (v) => (v || "").toLowerCase().trim().replace(/\s+/g, "-");

  useEffect(() => {
    fetchStats();
    fetchRecentGames();
    fetchFeaturedGames();
    // Load page size preference from settings
    fetchPageSizePreference();
    fetchBrowseUnread();
    (async () => {
      try {
        const res = await axios.get(`${API}/platforms`);
        const map = {};
        (res.data || []).forEach((p) => {
          if (!p.iconUrl) return;
          map[normKey(p.id)] = p.iconUrl;
          map[normKey(p.name)] = p.iconUrl;
        });
        setPlatformIcons(map);
      } catch (e) { /* silent */ }
    })();
  }, []);

  const fetchBrowseUnread = async () => {
    try {
      const res = await axios.get(`${API}/master/browse/notifications/summary`);
      setUnreadIds(res.data?.unreadIds || []);
    } catch (e) { /* silent */ }
  };

  const markBrowseRead = (id) => {
    if (!unreadSet.has(id)) return;
    setUnreadIds((prev) => prev.filter((x) => x !== id));
    setNewGames((prev) => prev.filter((g) => g.id !== id));
    axios.post(`${API}/master/browse/notifications/read`, { itemId: id })
      .then(() => window.dispatchEvent(new Event("unread-refresh")))
      .catch(() => {});
  };

  const toggleShowNewOnly = async () => {
    const next = !showNewOnly;
    setShowNewOnly(next);
    if (next) {
      setLoadingNewGames(true);
      try {
        const res = await axios.get(`${API}/master/browse/notifications/games`);
        setNewGames(res.data?.games || []);
      } catch (e) { /* silent */ }
      finally { setLoadingNewGames(false); }
    }
  };

  const clearAllBrowseNew = async () => {
    setUnreadIds([]);
    setNewGames([]);
    setShowNewOnly(false);
    try {
      await axios.post(`${API}/master/browse/notifications/read-all`);
      window.dispatchEvent(new Event("unread-refresh"));
    } catch (e) { /* silent */ }
  };

  // Reset page and fetch when filters change
  useEffect(() => {
    setCurrentPage(1);
    fetchGames(1);
  }, [debouncedSearch, selectedPlatform, selectedSystem, selectedRegion, selectedMediaType, selectedOwnership, sortBy, sortOrder, pageSize]);

  const fetchPageSizePreference = async () => {
    try {
      const response = await axios.get(`${API}/settings/preferences`);
      if (response.data.gamesPerPage) {
        setPageSize(response.data.gamesPerPage);
        localStorage.setItem("browsePageSize", response.data.gamesPerPage.toString());
      }
    } catch (error) {
      // Use default
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/master/browse/stats`);
      setStats(response.data);
      axios.get(`${API}/regions`).then((r) => {
        const map = {};
        (r.data || []).forEach((reg) => { map[reg.code] = reg.name; });
        setRegionNames(map);
      }).catch(() => {});
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentGames = async () => {
    try {
      const response = await axios.get(`${API}/master/browse/recent?limit=100`);
      setRecentGames(response.data);
    } catch (error) {
      console.error("Failed to fetch recent games:", error);
    }
  };

  // Featured Games — curated list toggled from the MDB edit dialog. Hard-
  // capped at 50 server-side (see routes/master_db.py::FEATURED_GAMES_MAX)
  // so this fetch never returns more than that. Rendered as a second flip-
  // tile row above Recently Added, sharing the same 5-tile grid.
  const fetchFeaturedGames = async () => {
    try {
      const response = await axios.get(`${API}/master/games/featured`);
      setFeaturedGames(response.data?.games || []);
    } catch (error) {
      console.error("Failed to fetch featured games:", error);
    }
  };

  const fetchGames = useCallback(async (page = currentPage) => {
    setLoadingGames(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.append("search", debouncedSearch);
      if (selectedPlatform !== "all") params.append("platform", selectedPlatform);
      if (selectedSystem !== "all") params.append("system", selectedSystem);
      if (selectedRegion !== "all") params.append("region", selectedRegion);
      if (selectedMediaType !== "all") params.append("mediaType", selectedMediaType);
      if (selectedOwnership !== "all") params.append("ownership", selectedOwnership);
      params.append("sort", sortBy);
      params.append("order", sortOrder);
      params.append("limit", pageSize.toString());
      params.append("skip", ((page - 1) * pageSize).toString());

      const response = await axios.get(`${API}/master/browse/games?${params.toString()}`);
      
      setGames(response.data.games);
      setTotal(response.data.total);
      setTotalPages(response.data.totalPages || Math.ceil(response.data.total / pageSize));
    } catch (error) {
      console.error("Failed to fetch games:", error);
    } finally {
      setLoadingGames(false);
    }
  }, [debouncedSearch, selectedPlatform, selectedSystem, selectedRegion, selectedMediaType, selectedOwnership, sortBy, sortOrder, pageSize, currentPage]);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchGames(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(parseInt(newSize, 10));
    localStorage.setItem("browsePageSize", newSize);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedPlatform("all");
    setSelectedSystem("all");
    setSelectedRegion("all");
    setSelectedMediaType("all");
    setSelectedOwnership("all");
    setSortBy("name");
    setSortOrder("asc");
  };

  const hasActiveFilters = searchQuery || selectedPlatform !== "all" || selectedSystem !== "all" || selectedRegion !== "all" || selectedMediaType !== "all" || selectedOwnership !== "all";

  // Get available systems for selected platform
  const getAvailableSystems = () => {
    if (!stats || selectedPlatform === "all") return [];
    const platform = stats.platforms[selectedPlatform];
    if (!platform) return [];
    return Object.entries(platform.systems).sort((a, b) => b[1] - a[1]);
  };

  return (
    <div className="space-y-6" data-testid="browse-page">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-white tracking-tight">
          Browse
        </h1>
        <p className="text-white/50 mt-1">Explore the community game library</p>
      </div>

      {/* Stats Overview */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
        </div>
      ) : stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Total Stats Card */}
          <Card className="bg-[#0A0A0A] border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Database className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-white/50 text-sm">Total Games</p>
                  <p className="text-white text-2xl font-bold">{stats.totalGames?.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-white/50 text-sm">Total Achievements</p>
                  <p className="text-white text-2xl font-bold">{stats.totalAchievements?.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <Gamepad2 className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-white/50 text-sm">Platforms</p>
                  <p className="text-white text-2xl font-bold">{Object.keys(stats.platforms).length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Platform Breakdown + Recently Added — side by side */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Platform Breakdown */}
          <div className="space-y-2">
            <h2 className="text-white text-sm font-medium text-white/70">Platform Breakdown</h2>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(stats.platforms)
                .sort((a, b) => a[0].toLowerCase().localeCompare(b[0].toLowerCase()))
                .map(([platform, data]) => {
                  // Normalize platform key with aliases
                  const BROWSE_ALIASES = {
                    "epic games": "epic",
                    "card & board": "card-&-board-games",
                    "card & board games": "card-&-board-games",
                    "google play": "google-play",
                  };
                  const platformKey = BROWSE_ALIASES[platform.toLowerCase()] || platform.toLowerCase();
                  // Admin-uploaded logo (shared app-wide) — matched by name/key; generic icon otherwise.
                  const logoUrl = platformIcons[normKey(platform)] || platformIcons[normKey(platformKey)];
                  
                  return (
                    <button
                      key={platform}
                      onClick={() => {
                        setSelectedPlatform(platform === selectedPlatform ? "all" : platform);
                        setSelectedSystem("all");
                        document.querySelector('[data-testid="browse-search-input"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                        selectedPlatform === platform
                          ? 'bg-purple-500/20 border-purple-500/50 text-white'
                          : 'bg-[#0A0A0A] border-white/10 text-white/70 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      {logoUrl ? (
                        <img src={logoUrl} alt={platform} className="w-5 h-5 object-contain" />
                      ) : (
                        <Gamepad2 className="w-5 h-5" />
                      )}
                      <span className="text-sm capitalize">{platform}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-white/10">{data.total.toLocaleString()}</span>
                    </button>
                  );
                })}
            </div>
          </div>

          {/* Featured Games + Recently Added — stacked in the right column.
              Featured is the curated hero row (max 50 games, admin-toggled),
              Recently Added shows the latest additions to the MDB. Both use
              the same 5-column flip-tile grid. */}
          <div className="space-y-4">
            {featuredGames.length > 0 && (
              <div className="space-y-2" data-testid="featured-games-row">
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-amber-400" />
                  <h2 className="text-white text-sm font-medium">Featured Games</h2>
                </div>
                <div className="grid grid-cols-5 gap-3">
                  {Array.from({ length: 5 }).map((_, col) => {
                    const tileGames = featuredGames.filter((_, i) => i % 5 === col);
                    if (tileGames.length === 0) return <div key={`f-empty-${col}`} />;
                    return (
                      <FlipTile
                        key={`f-${col}`}
                        games={tileGames}
                        interval={7000 + col * 350}
                        delay={2000 + col * 600}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Recently Added — animated flip tiles */}
            {recentGames.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-400" />
                  <h2 className="text-white text-sm font-medium">Recently Added</h2>
                </div>
                <div className="grid grid-cols-5 gap-3">
                  {Array.from({ length: 5 }).map((_, col) => {
                    // Each tile gets every 5th game: tile 0 gets [0,5,10,...], tile 1 gets [1,6,11,...] etc.
                    const tileGames = recentGames.filter((_, i) => i % 5 === col);
                    return (
                      <FlipTile
                        key={col}
                        games={tileGames}
                        interval={6000 + col * 300}
                        delay={5000 + col * 800}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search & Filter Section */}
      <Card className="bg-[#0A0A0A] border-white/5">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <Filter className="w-5 h-5 text-purple-400" />
            Browse Games
            {total > 0 && (
              <span className="text-white/40 font-normal text-sm ml-2">
                ({total.toLocaleString()} {total === 1 ? 'game' : 'games'})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name..."
                className="pl-10 bg-[#121212] border-white/10 text-white"
                data-testid="browse-search-input"
              />
            </div>

            <Select value={selectedPlatform} onValueChange={(v) => { setSelectedPlatform(v); setSelectedSystem("all"); }}>
              <SelectTrigger className="w-[150px] bg-[#121212] border-white/10 text-white" data-testid="browse-platform-select">
                <SelectValue placeholder="Platform" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                <SelectItem value="all">All Platforms</SelectItem>
                {stats && Object.keys(stats.platforms).sort().map((platform) => (
                  <SelectItem key={platform} value={platform} className="capitalize">
                    {platform}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedPlatform !== "all" && getAvailableSystems().length > 0 && (
              <Select value={selectedSystem} onValueChange={setSelectedSystem}>
                <SelectTrigger className="w-[180px] bg-[#121212] border-white/10 text-white" data-testid="browse-system-select">
                  <SelectValue placeholder="System" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="all">All Systems</SelectItem>
                  {getAvailableSystems().map(([system, count]) => (
                    <SelectItem key={system} value={system}>
                      {system} ({count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Region Filter */}
            {stats?.regions && Object.keys(stats.regions).length > 0 && (
              <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                <SelectTrigger className="w-[140px] bg-[#121212] border-white/10 text-white" data-testid="browse-region-select">
                  <Globe className="w-4 h-4 mr-1 text-white/50" />
                  <SelectValue placeholder="Region" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="all">All Regions</SelectItem>
                  {Object.entries(stats.regions)
                    .map(([region, count]) => ({
                      code: region,
                      count,
                      label: regionNames[region] || REGION_LABELS[region] || region,
                    }))
                    .sort((a, b) =>
                      (a.code === "WW" ? 0 : 1) - (b.code === "WW" ? 0 : 1) ||
                      a.label.localeCompare(b.label)
                    )
                    .map(({ code, count, label }) => (
                      <SelectItem key={code} value={code}>
                        {label} ({count})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}

            {/* Media Type Filter */}
            <Select value={selectedMediaType} onValueChange={setSelectedMediaType}>
              <SelectTrigger className="w-[150px] bg-[#121212] border-white/10 text-white" data-testid="browse-media-type-select">
                <SelectValue placeholder="Media Type" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                <SelectItem value="all">All Media</SelectItem>
                <SelectItem value="physical">Physical</SelectItem>
                <SelectItem value="digital">Digital Only</SelectItem>
                <SelectItem value="both">Physical + Digital</SelectItem>
              </SelectContent>
            </Select>

            {/* Ownership Filter — cross-references your library */}
            <Select value={selectedOwnership} onValueChange={setSelectedOwnership}>
              <SelectTrigger className="w-[160px] bg-[#121212] border-white/10 text-white" data-testid="browse-ownership-select">
                <SelectValue placeholder="Ownership" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                <SelectItem value="all">All Games</SelectItem>
                <SelectItem value="in_library">In My Library</SelectItem>
                <SelectItem value="owned">Owned</SelectItem>
                <SelectItem value="not_in_library">Not in My Library</SelectItem>
                <SelectItem value="not_owned">Not Owned</SelectItem>
                <SelectItem value="wishlist">On My Wish List</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[130px] bg-[#121212] border-white/10 text-white" data-testid="browse-sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="createdAt">Date Added</SelectItem>
                <SelectItem value="achievementCount">Achievements</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="icon"
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              className="bg-[#121212] border-white/10 text-white hover:bg-white/10"
              data-testid="browse-sort-order"
            >
              {sortOrder === "asc" ? "↑" : "↓"}
            </Button>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-white/60 hover:text-white"
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}

            {/* Request a game */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setGameRequestOpen(true)}
              className="ml-auto border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 shrink-0"
              data-testid="request-game-btn"
            >
              <MessageSquarePlus className="w-4 h-4 mr-1.5" />
              Don't see a game?
            </Button>
          </div>

          {/* Games Grid */}
          {unreadIds.length > 0 && (
            <div className="flex items-center justify-between mb-4 px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20" data-testid="browse-unread-banner">
              <span className="text-red-200 text-sm font-medium">
                {unreadIds.length} new game{unreadIds.length > 1 ? "s" : ""} added
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  className={`h-8 ${showNewOnly ? "text-white bg-red-500/25" : "text-red-300 hover:text-white hover:bg-red-500/20"}`}
                  onClick={toggleShowNewOnly}
                  data-testid="browse-show-new-only-toggle"
                >
                  {showNewOnly ? "Showing new only" : "Show new only"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-300 hover:text-white hover:bg-red-500/20 h-8"
                  onClick={clearAllBrowseNew}
                  data-testid="browse-clear-new-btn"
                >
                  Clear new
                </Button>
              </div>
            </div>
          )}
          {(showNewOnly ? loadingNewGames : (loadingGames && games.length === 0)) ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : (showNewOnly ? newGames.length === 0 : games.length === 0) ? (
            <div className="text-center py-12">
              <Gamepad2 className="w-12 h-12 mx-auto mb-4 text-white/20" />
              <p className="text-white/50">{showNewOnly ? "No new games" : "No games found"}</p>
              {!showNewOnly && hasActiveFilters && (
                <Button variant="link" onClick={clearFilters} className="text-purple-400 mt-2">
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {(showNewOnly ? newGames : games).map((game) => (
                  <Link
                    key={game.id}
                    to={browseTarget(game)}
                    onClick={() => markBrowseRead(game.id)}
                    className={`flex gap-3 p-3 bg-[#121212] rounded-lg border transition-colors group ${unreadSet.has(game.id) ? "border-red-500/40" : "border-white/5 hover:border-white/20"}`}
                    data-testid={`browse-game-${game.id}`}
                  >
                    <div className="w-16 h-24 flex-shrink-0 overflow-hidden rounded bg-black/50">
                      {game.boxArtUrl ? (
                        <img
                          src={game.boxArtUrl}
                          alt={game.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Gamepad2 className="w-6 h-6 text-white/20" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium line-clamp-2 group-hover:text-purple-300 transition-colors">
                        {unreadSet.has(game.id) && (
                          <span className="mr-1.5 text-[10px] px-1.5 py-0.5 rounded bg-red-500 text-white font-bold align-middle" data-testid={`browse-new-badge-${game.id}`}>New!</span>
                        )}
                        {game.name}
                      </p>
                      
                      {/* Platforms and Systems */}
                      <div className="flex flex-wrap items-center gap-1 mt-1">
                        {game.platforms?.map(p => (
                          <PlatformBadge key={p} platform={p} size="sm" />
                        ))}
                        {/* Show ONLY the primary system on Browse tiles.
                            The full "playable on" list is reserved for the
                            GameDetail page so tiles stay compact. */}
                        {(() => {
                          const primary = game.primarySystem || (game.systems && game.systems[0]);
                          if (!primary) return null;
                          return <SystemBadge key={primary} system={primary} size="sm" />;
                        })()}
                        {(() => {
                          const list = (game.regionList && game.regionList.length
                            ? game.regionList
                            : (game.region ? [game.region] : [])
                          ).filter((r) => r && r !== "WW");
                          if (!list.length) return null;
                          return (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 border border-blue-500/30"
                              data-testid={`browse-region-${game.id}`}
                              title={`Region${list.length > 1 ? "s" : ""}: ${list.join(", ")}`}
                            >
                              {list.join(" · ")}
                            </span>
                          );
                        })()}
                        {game.isAchievementSlop && <SlopBadge compact />}
                        {game.isUnplayable && <UnplayableBadge compact />}
                        {game.versionCount > 1 && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 font-medium flex items-center gap-1"
                            data-testid={`browse-versions-${game.id}`}
                            title={`${game.versionCount} linked versions — click to compare`}
                          >
                            <Layers className="w-2.5 h-2.5" />
                            {game.versionCount} versions
                          </span>
                        )}
                        {(game.relationTags || []).map(tag => (
                          <span
                            key={tag}
                            className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${RELATION_TAG_STYLES[tag] || "bg-white/10 text-white/60 border-white/20"}`}
                            data-testid={`browse-reltag-${game.id}-${tag}`}
                            title={`This game is a ${tag === "Alt" ? "alternate version" : tag.toLowerCase()} — see related games on its page`}
                          >
                            {tag === "Alt" ? "Alt Version" : tag}
                          </span>
                        ))}
                        {game.inWishlist && !game.inLibrary && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 font-medium"
                            data-testid={`browse-wishlist-${game.id}`}
                          >
                            Wishlist
                          </span>
                        )}
                        {game.inLibrary && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/30 font-medium"
                            data-testid={`browse-in-library-${game.id}`}
                          >
                            In Library{game.libraryCount > 1 ? ` ×${game.libraryCount}` : ""}
                          </span>
                        )}
                        {game.owned && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-300 border border-green-500/30 font-medium"
                            data-testid={`browse-owned-${game.id}`}
                          >
                            Owned{game.ownedCount > 1 ? ` ×${game.ownedCount}` : ""}
                          </span>
                        )}
                      </div>
                      
                      {/* Stats Row */}
                      <div className="flex items-center gap-3 mt-2 text-xs text-white/50">
                        {(() => {
                          const yr = (game.releaseDate || "").match(/\d{4}/)?.[0];
                          return yr ? (
                            <span className="flex items-center gap-1" data-testid={`browse-year-${game.id}`}>
                              <Calendar className="w-3 h-3" />
                              {yr}
                            </span>
                          ) : null;
                        })()}
                        {game.totalAchievements > 0 && (
                          <span className="flex items-center gap-1">
                            <Trophy className="w-3 h-3" />
                            {game.totalAchievements}
                          </span>
                        )}
                        {game.totalScore > 0 && (
                          <span className="text-yellow-400 font-medium">{game.totalScore.toLocaleString()}G</span>
                        )}
                      </div>
                      
                      {/* Developer/Publisher */}
                      {(game.developers?.length > 0 || game.developer || game.publishers?.length > 0 || game.publisher) && (
                        <p className="text-[10px] text-white/30 mt-1 truncate">
                          {game.developers?.length > 0 ? game.developers.join(", ") : game.developer}
                          {(game.publishers?.length > 0 || game.publisher) && (game.developers?.length > 0 || game.developer) ? " • " : ""}
                          {game.publishers?.length > 0 ? game.publishers.join(", ") : game.publisher}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {!showNewOnly && totalPages > 1 && (
                <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-white/10">
                  {/* Page size selector */}
                  <div className="flex items-center gap-2">
                    <span className="text-white/50 text-sm">Show:</span>
                    <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-20 h-8 bg-[#121212] border-white/10 text-white text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Page navigation */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1 || loadingGames}
                      className="h-8 w-8 p-0 bg-[#121212] border-white/10"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {/* First page */}
                      {currentPage > 2 && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => goToPage(1)}
                            className="h-8 w-8 p-0 bg-[#121212] border-white/10"
                          >
                            1
                          </Button>
                          {currentPage > 3 && <span className="text-white/30 px-1">...</span>}
                        </>
                      )}
                      
                      {/* Pages around current */}
                      {[currentPage - 1, currentPage, currentPage + 1]
                        .filter(p => p >= 1 && p <= totalPages)
                        .map(page => (
                          <Button
                            key={page}
                            variant={page === currentPage ? "default" : "outline"}
                            size="sm"
                            onClick={() => goToPage(page)}
                            disabled={loadingGames}
                            className={`h-8 w-8 p-0 ${
                              page === currentPage 
                                ? 'bg-purple-600 border-purple-600' 
                                : 'bg-[#121212] border-white/10'
                            }`}
                          >
                            {page}
                          </Button>
                        ))}
                      
                      {/* Last page */}
                      {currentPage < totalPages - 1 && (
                        <>
                          {currentPage < totalPages - 2 && <span className="text-white/30 px-1">...</span>}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => goToPage(totalPages)}
                            className="h-8 w-8 p-0 bg-[#121212] border-white/10"
                          >
                            {totalPages}
                          </Button>
                        </>
                      )}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages || loadingGames}
                      className="h-8 w-8 p-0 bg-[#121212] border-white/10"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Page info */}
                  <span className="text-white/50 text-sm">
                    Page {currentPage} of {totalPages}
                  </span>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* AdSense */}
      <AdBanner />

      {/* Game Request Dialog */}
      <GameRequestDialog open={gameRequestOpen} onOpenChange={setGameRequestOpen} />
    </div>
  );
}
